import type { ReactElement } from 'react';
import type { CalendarEntry, MealSlot } from '../../api/calendarTypes.js';
import {
  MEAL_DEFAULT_TIME,
  MEAL_ICON,
  MEAL_LABEL,
} from '../../api/calendarTypes.js';

function formatWeight(grams: number): string {
  if (grams >= 1000) return `${(grams / 1000).toFixed(2)} kg`;
  return `${Math.round(grams)} g`;
}

export interface MealCardProps {
  slot: MealSlot;
  entry: CalendarEntry | undefined;
  onOpenRecipe: (recipeId: number) => void;
  onCook: (entry: CalendarEntry) => void;
  onEdit?: (entry: CalendarEntry) => void;
  onRemove?: () => void;
  removing?: boolean;
}

export function MealCard({
  slot,
  entry,
  onOpenRecipe,
  onCook,
  onEdit,
  onRemove,
  removing,
}: MealCardProps): ReactElement {
  if (!entry) {
    return (
      <div
        className="flex min-h-[88px] flex-col items-center justify-center gap-1 rounded-2xl border border-dashed p-3 text-center"
        style={{
          borderColor: 'var(--border)',
          color: 'var(--text-muted)',
          background: 'transparent',
        }}
        data-testid={`meal-card-${slot}-empty`}
      >
        <span className="text-sm">
          {MEAL_ICON[slot]} {MEAL_LABEL[slot]} ({MEAL_DEFAULT_TIME[slot]}) — Add meal
        </span>
      </div>
    );
  }

  const leftoverStyle = entry.is_leftover
    ? { opacity: 0.6, textDecoration: 'line-through' as const }
    : undefined;

  return (
    <div
      className="flex flex-col gap-2 rounded-2xl border p-3"
      style={{
        borderColor: 'var(--border)',
        background: 'var(--bg-surface)',
      }}
      data-testid={`meal-card-${slot}`}
      data-leftover={entry.is_leftover ? 'true' : 'false'}
    >
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold" style={{ color: 'var(--text-main)' }}>
          <span aria-hidden="true">{MEAL_ICON[slot]}</span> {MEAL_LABEL[slot]} (
          {MEAL_DEFAULT_TIME[slot]})
        </div>
        <div className="flex items-center gap-2">
          {entry.is_leftover ? (
            <span
              className="rounded-full px-2 py-0.5 text-[10px] uppercase"
              style={{
                background: 'color-mix(in srgb, var(--accent) 15%, transparent)',
                color: 'var(--accent)',
              }}
            >
              leftover
            </span>
          ) : null}
          {onEdit && !entry.is_leftover ? (
            <button
              type="button"
              onClick={() => onEdit(entry)}
              aria-label={`Edit ${entry.recipe.title}`}
              className="flex min-h-[28px] min-w-[28px] items-center justify-center rounded-md border text-sm"
              style={{
                borderColor: 'var(--border)',
                color: 'var(--text-muted)',
                background: 'transparent',
              }}
              data-testid={`meal-card-${slot}-edit`}
            >
              ✏
            </button>
          ) : null}
          {onRemove ? (
            <button
              type="button"
              onClick={onRemove}
              disabled={removing}
              aria-label={`Remove ${entry.recipe.title}`}
              className="flex min-h-[28px] min-w-[28px] items-center justify-center rounded-md border text-sm"
              style={{
                borderColor: 'var(--border)',
                color: 'var(--text-muted)',
                background: 'transparent',
              }}
              data-testid={`meal-card-${slot}-remove`}
            >
              🗑
            </button>
          ) : null}
        </div>
      </div>
      <div style={leftoverStyle}>
        <div className="text-base font-semibold" style={{ color: 'var(--text-main)' }}>
          {entry.recipe.title}
        </div>
        <div className="flex gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
          <span>{entry.total_kcal} kcal</span>
          <span aria-hidden="true">·</span>
          <span data-testid={`meal-card-${slot}-weight`}>{formatWeight(entry.total_weight_g)}</span>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onOpenRecipe(entry.recipe.id)}
          className="flex min-h-[48px] flex-1 items-center justify-center rounded-md border text-sm"
          style={{
            borderColor: 'var(--border)',
            color: 'var(--text-main)',
            background: 'transparent',
          }}
          data-testid={`meal-card-${slot}-recipe`}
        >
          📖 Recipe
        </button>
        <button
          type="button"
          onClick={() => onCook(entry)}
          className="flex min-h-[48px] flex-1 items-center justify-center rounded-md border text-sm font-medium"
          style={{
            borderColor: 'var(--accent)',
            color: 'var(--accent)',
            background: 'color-mix(in srgb, var(--accent) 10%, transparent)',
          }}
          data-testid={`meal-card-${slot}-cook`}
        >
          👨‍🍳 Cook
        </button>
      </div>
    </div>
  );
}
